import React from "react";
import { LandingNavbar } from "@/components/landing-navbar";
import { Footer } from "@/components/footer";
import { SEO, schemas } from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { ArrowRight, Users, Check, Shield, Lock } from "lucide-react";
import { Link } from "react-router-dom";

export default function StaffManagementPage() {
  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Staff Management for Cleaning Companies"
        description="Manage employees and subcontractors with ease. Role-based permissions, team profiles, and secure access control for your janitorial business."
        keywords={[
          "staff management",
          "employee management cleaning",
          "janitorial workforce management",
          "cleaning crew management",
        ]}
        url="/features/staff-management"
        structuredData={[
          schemas.createBreadcrumbSchema([
            { name: "Home", url: "/" },
            { name: "Features", url: "/features/staff-management" },
            { name: "Staff Management", url: "/features/staff-management" },
          ]),
          schemas.createFeatureModuleSchema({
            name: "Staff & Subcontractor Hub",
            description:
              "Manage commercial cleaning employees and 1099 subcontractors with granular role-based permissions, insurance compliance tracking, and crew performance metrics.",
            url: "/features/staff-management",
            featureList: [
              "Granular Role-Based Permissions (Admin, Manager, Tech)",
              "1099 Subcontractor Directory & COI Tracking",
              "Individual & Team Activity Auditing",
              "Time & Attendance Tracking",
              "Integrated Crew Communication Channels",
            ],
          }),
        ]}
      />
      <LandingNavbar />

      {/* Hero Section */}
      <section className="relative overflow-hidden pb-20 pt-32">
        <div className="container mx-auto px-4">
          <div className="flex flex-col items-center gap-12 lg:flex-row">
            <div className="space-y-8 lg:w-1/2">
              <div className="inline-flex items-center gap-2 rounded-full border border-blue-100 bg-blue-50 px-3 py-1 text-sm font-medium text-blue-600">
                <Users className="h-4 w-4" />
                <span>Team & Role Management</span>
              </div>

              <h1 className="text-4xl font-bold leading-[1.1] tracking-tight text-slate-900 md:text-6xl">
                Empower Your{" "}
                <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                  Workforce
                </span>
              </h1>

              <p className="max-w-xl text-lg leading-relaxed text-slate-600">
                Manage employees and subcontractors with ease. Secure your business data with
                granular role-based permissions.
              </p>

              <div className="flex flex-wrap gap-4">
                <Link to="/sign-up">
                  <Button
                    size="lg"
                    className="h-12 rounded-full bg-blue-600 px-8 text-lg hover:bg-blue-700"
                  >
                    Get Started Free <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </div>

            <div className="relative lg:w-1/2">
              <div className="relative aspect-[16/9] overflow-hidden rounded-2xl border border-slate-200/50 bg-white/50 shadow-2xl backdrop-blur-sm">
                <img
                  src="/assets/feature_staff.png"
                  alt="Staff Management Interface"
                  className="h-full w-full object-cover"
                />
              </div>
              {/* Decorative elements */}
              <div className="absolute -bottom-12 -right-12 -z-10 h-64 w-64 rounded-full bg-green-500/10 blur-3xl" />
            </div>
          </div>
        </div>
      </section>

      {/* Feature Deep Dive */}
      <section className="bg-slate-50 py-20">
        <div className="container mx-auto px-4">
          <div className="mx-auto mb-16 max-w-3xl text-center">
            <h2 className="mb-4 text-3xl font-bold text-slate-900">
              Secure & Scalable Team Management
            </h2>
            <p className="text-slate-600">
              From a single owner-operator to a large enterprise, ManageClean OS scales with your
              team.
            </p>
          </div>

          <div className="mx-auto grid max-w-4xl gap-8 md:grid-cols-2">
            <motion.div
              whileHover={{ y: -5 }}
              className="rounded-2xl border border-slate-100 bg-white p-8 shadow-sm"
            >
              <div className="mb-6 flex h-12 w-12 items-center justify-center rounded-xl bg-blue-100">
                <Users className="h-6 w-6 text-blue-600" />
              </div>
              <h3 className="mb-3 text-xl font-bold text-slate-900">Comprehensive Profiles</h3>
              <p className="mb-4 text-slate-600">
                Maintain detailed records for both internal employees and external subcontractors.
                Keep track of contact info, roles, and status.
              </p>
              <ul className="space-y-2">
                <li className="flex items-center text-sm text-slate-600">
                  <Check className="mr-2 h-4 w-4 text-green-500" /> Employee Database
                </li>
                <li className="flex items-center text-sm text-slate-600">
                  <Check className="mr-2 h-4 w-4 text-green-500" /> Subcontractor Management
                </li>
              </ul>
            </motion.div>

            <motion.div
              whileHover={{ y: -5 }}
              className="rounded-2xl border border-slate-100 bg-white p-8 shadow-sm"
            >
              <div className="mb-6 flex h-12 w-12 items-center justify-center rounded-xl bg-indigo-100">
                <Lock className="h-6 w-6 text-indigo-600" />
              </div>
              <h3 className="mb-3 text-xl font-bold text-slate-900">Role-Based Access Control</h3>
              <p className="mb-4 text-slate-600">
                Protect your sensitive data. Ensure staff only see what they need to do their job
                with granular permission settings.
              </p>
              <ul className="space-y-2">
                <li className="flex items-center text-sm text-slate-600">
                  <Check className="mr-2 h-4 w-4 text-green-500" /> Admin & Manager Roles
                </li>
                <li className="flex items-center text-sm text-slate-600">
                  <Check className="mr-2 h-4 w-4 text-green-500" /> Field Access Permissions
                </li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
